// @flow
import {ofType, StateObservable} from 'redux-observable';
import {map, switchMap, debounceTime, delay} from 'rxjs/operators';
import {buildRoute} from '../../http';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';

import {endpoints, listProductsApi, sortProductApi} from './api';

import type {RootState} from '../index';
import type {ActionsObservable} from 'redux-observable';
import type {Action} from '../../store/utils';
import type {MinimalProduct, Pagination} from '../../entities';
import {arrayToOrderedMap, orderedMapToArray} from '../../utils/array';
import {List} from 'immutable';

export type OrganizeProductsAction = Action & {
  queryFilters?: {[key: string]: string},
  resetProducts?: boolean,
  sortRequest?: {[key: string]: string},
  sortMovement?: {[key: string]: string},
};

export type OrganizeProductsState = {
  isFetching?: boolean,
  products?: MinimalProduct[],
  pagination?: Pagination,
  fetchProductsError?: string,
  sortProductsError?: string,
  isSorting?: boolean,
};

export const SIZE = 50;
// Fetch product actions
export class FetchOrganizedProducts extends ActionCreator<OrganizeProductsAction> {}
export class FetchOrganizedProductsSuccess extends ActionCreator<OrganizeProductsAction> {}
export class FetchOrganizedProductsError extends ActionCreator<OrganizeProductsAction> {}

// Sort product actions
export class SortProducts extends ActionCreator<OrganizeProductsAction> {}
export class SortProduct extends ActionCreator<OrganizeProductsAction> {}
export class SortProductSuccess extends ActionCreator<OrganizeProductsAction> {}
export class SortProductError extends ActionCreator<OrganizeProductsAction> {}

export const actions = {
  fetchOrganizedProducts: (payload: OrganizeProductsAction) =>
    new FetchOrganizedProducts(payload),
  sortProducts: (payload: OrganizeProductsAction) => new SortProducts(payload),
  sortProduct: (payload: OrganizeProductsAction) => new SortProduct(payload),
};

export const initialState: OrganizeProductsState = {};

export default createReducer(initialState, {
  [FetchOrganizedProducts.type]: (
    state: OrganizeProductsState,
    action: OrganizeProductsAction
  ) => ({
    ...state,
    isFetching: true,
    fetchProductsError: undefined,
    products: action.reset ? undefined : state.products,
    pagination: action.reset ? undefined : state.pagination,
  }),
  [FetchOrganizedProductsSuccess.type]: (
    state: OrganizeProductsState,
    action: OrganizeProductsAction
  ) => {
    const oldProducts = arrayToOrderedMap(state.products);
    const newProducts = arrayToOrderedMap(action.products);
    const products = orderedMapToArray(oldProducts.merge(newProducts));

    const newState = {
      ...state,
      isFetching: false,
      products,
      pagination: action.pagination,
    };

    return newState;
  },
  [FetchOrganizedProductsError.type]: (
    state: OrganizeProductsState,
    action: OrganizeProductsAction
  ) => ({
    ...state,
    isFetching: false,
    fetchProductsError: action.error,
  }),
  // Sort product
  [SortProducts.type]: (
    state: OrganizeProductsState,
    action: OrganizeProductsAction
  ) => {
    const {pos, item} = action.sortMovement;
    const indexElementSort = state.products
      ? state.products.findIndex(el => el.id === item.id)
      : -1;
    const productsList = List(state.products)
      .delete(indexElementSort)
      .insert(pos, item);
    const products = productsList.toArray();

    return {
      ...state,
      products,
      sortProductSuccess: undefined,
      sortProductError: undefined,
    };
  },
  [SortProduct.type]: (state: OrganizeProductsState) => ({
    ...state,
    isSorting: true,
    sortProductSuccess: undefined,
    sortProductError: undefined,
  }),
  [SortProductSuccess.type]: (state: OrganizeProductsState) => ({
    ...state,
    isSorting: false,
    sortProductError: undefined,
  }),
  [SortProductError.type]: (
    state: OrganizeProductsState,
    action: OrganizeProductsAction
  ) => ({
    ...state,
    isSorting: false,
    sortProductError: action.error,
  }),
});

export function fetchOrganizedProductsEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchOrganizedProducts.type),
    debounceTime(500),
    switchMap(({queryFilters}: OrganizeProductsAction) => {
      const pagination = state$.value.organizeProducts.pagination;
      const qf = !pagination
        ? {...queryFilters, page: 0, size: SIZE}
        : {...queryFilters, page: pagination.page + 1, size: SIZE};

      return listProductsApi({...qf, product_type: 'MINIMAL'}).pipe(
        map(
          ({
            data,
            pagination,
          }: {
            data: MinimalProduct[],
            pagination: Pagination,
          }) =>
            new FetchOrganizedProductsSuccess({
              products: data,
              pagination,
            })
        ),
        buildCatchError(
          FetchOrganizedProductsError,
          'Error al obtener productos'
        )
      );
    })
  );
}

export function sortProductEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SortProduct.type),
    switchMap(({sortRequest, categories}) => {
      const {init, end} = sortRequest;
      return sortProductApi(
        {productId: end.id},
        {
          route: buildRoute(endpoints.SORT_PRODUCT, [init.id]),
          queryParams:
            categories && categories.length > 0 ? {categories} : undefined,
        }
      ).pipe(
        delay(350),
        map(() => new SortProductSuccess()),
        buildCatchError(SortProductError)
      );
    })
  );
}
